import { useQuery } from "@tanstack/react-query";
import {
  getJobRunTrends,
  getJobs,
  getJobsSummary,
  getJobCostTrends,
  getDatabricksJobDetail,
  getDatabricksJobRunClusters,
  getDatabricksJobRuns,
  getDatabricksJobStats,
  getDatabricksSingleJobRunTrends,
  getDatabricksResourceOpportunities,
} from "@/helpers/apis";
import { ResourceType } from "@/pages/explorer/types";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useInsightState } from "@/modules/insights/context";
import type {
  JobRunTrendsParams,
  JobsParams,
  JobsSummaryParams,
  JobCostTrendsParams,
} from "../types";

const JOBS_QUERY_KEYS = {
  runTrends: "jobs-run-trends",
  costTrends: "jobs-cost-trends",
  summary: "jobs-summary",
  list: "jobs-list",
  detail: "jobs-detail",
  runs: "jobs-runs",
  stats: "jobs-stats",
  singleJobTrends: "jobs-single-trends",
  resourceOpportunities: "jobs-resource-opportunities",
  runClusters: "jobs-run-clusters",
} as const;

export const useJobRunTrends = (params: JobRunTrendsParams, enabled = true) => {
  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.runTrends,
      params.start_date,
      params.end_date,
      params.granularity,
      params.workspace_id,
    ],
    queryFn: () => getJobRunTrends(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });
};

export const useJobsSummary = (params: JobsSummaryParams, enabled = true) => {
  return useQuery({
    queryKey: [JOBS_QUERY_KEYS.summary, params.start_date, params.end_date, params.workspace_id],
    queryFn: () => getJobsSummary(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });
};

export const useJobCostTrends = (params: JobCostTrendsParams, enabled = true) => {
  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.costTrends,
      params.start_date,
      params.end_date,
      params.granularity,
      params.workspace_id,
    ],
    queryFn: () => getJobCostTrends(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });
};

export const useJobs = (params: JobsParams = {}, enabled = true) => {
  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.list,
      params.workspace_id,
      params.status,
      params.search,
      params.page,
      params.page_size,
      params.days,
      params.limit,
      params.sortAttribute,
      params.sortOrder,
      params.job_names,
      params.creators,
      params.workspaces,
      params.auto_tune_enabled,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getJobs(params),
    enabled,
    // Don't retry — the axios response interceptor in
    // ``helpers/api_helper.ts`` strips non-2xx responses to a plain
    // string (the backend's ``detail`` message) before react-query sees
    // them, so we can't reliably gate retry on status code. The most
    // common failure here is a user-correctable 422 ("filtered window
    // exceeds the auto_tune_savings sort cap, narrow filters"); retrying
    // only delays the message the user needs to see. Network blips can
    // be resolved by the user re-issuing the request.
    retry: false,
  });
};

export const useJobDetail = (
  params: { job_id: string; start_date?: string; end_date?: string },
  enabled = true
) => {
  return useQuery({
    queryKey: [JOBS_QUERY_KEYS.detail, params.job_id],
    queryFn: () =>
      getDatabricksJobDetail(params.job_id, {
        start_date: params.start_date,
        end_date: params.end_date,
      }),
    enabled: enabled && Boolean(params.job_id),
  });
};

export const useJobRuns = (
  params: {
    job_id: string;
    start_date?: string;
    end_date?: string;
    run_ids?: string[];
    trigger_types?: string[];
    result_state?: string[];
    cluster_ids?: string[];
    page?: number;
    page_size?: number;
    sortAttribute?: string;
    sortOrder?: "asc" | "desc" | "";
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.runs,
      params.job_id,
      params.start_date,
      params.end_date,
      params.run_ids,
      params.trigger_types,
      params.result_state,
      params.cluster_ids,
      params.page,
      params.page_size,
      params.sortAttribute,
      params.sortOrder,
    ],
    queryFn: () => {
      const { job_id, ...rest } = params;
      return getDatabricksJobRuns(job_id, rest);
    },
    enabled: enabled && Boolean(params.job_id),
  });
};

// Distinct cluster list for the Cluster filter dropdown. Independent of the
// runs query so the dropdown surfaces every cluster the job touched in the
// date window — not just the clusters that happen to be on the rendered page.
export const useJobRunClusters = (
  params: { job_id: string; start_date?: string; end_date?: string },
  enabled = true
) => {
  return useQuery({
    queryKey: [JOBS_QUERY_KEYS.runClusters, params.job_id, params.start_date, params.end_date],
    queryFn: () =>
      getDatabricksJobRunClusters(params.job_id, {
        start_date: params.start_date ?? "",
        end_date: params.end_date,
      }),
    enabled: enabled && Boolean(params.job_id && params.start_date),
  });
};

export const useJobStats = (
  jobId: string,
  params: { start_date?: string; end_date?: string } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [JOBS_QUERY_KEYS.stats, jobId, params.start_date, params.end_date],
    queryFn: () => getDatabricksJobStats(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

export const useSingleJobRunTrends = (
  jobId: string,
  params: { start_date?: string; end_date?: string; granularity?: "day" | "month" } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.singleJobTrends,
      jobId,
      params.start_date,
      params.end_date,
      params.granularity,
    ],
    queryFn: () => getDatabricksSingleJobRunTrends(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

export const useJobResourceOpportunities = ({
  resourceUri,
  instanceId,
  workspaceId,
  startDate: startDateProp,
  endDate: endDateProp,
}: {
  resourceUri: string;
  instanceId?: string;
  workspaceId?: string;
  /** Dates to fetch with; defaults to the unprefixed URL date range. */
  startDate?: Date;
  endDate?: Date;
}) => {
  const fallbackDates = useDateFromSearchParams();
  const startDate = startDateProp ?? fallbackDates.startDate;
  const endDate = endDateProp ?? fallbackDates.endDate;
  const {
    selectedTab: status,
    sort: { sortBy, sortOrder },
  } = useInsightState();

  return useQuery({
    queryKey: [
      JOBS_QUERY_KEYS.resourceOpportunities,
      resourceUri,
      ResourceType.Job,
      instanceId,
      workspaceId,
      startDate,
      endDate,
      status,
      sortBy,
      sortOrder,
    ],
    queryFn: () => {
      const start = getDateByStartOfDayWithoutTimeoffset(startDate);
      const end = getDateByEndOfDayWithoutTimeoffset(endDate);
      return getDatabricksResourceOpportunities({
        resource_uri: resourceUri,
        resource_type: ResourceType.Job,
        instance_id: instanceId || "",
        ...(workspaceId && { workspace_id: workspaceId }),
        start_date:
          start && !isNaN(start.getTime()) ? start.toISOString() : new Date().toISOString(),
        end_date: end && !isNaN(end.getTime()) ? end.toISOString() : new Date().toISOString(),
        // Backend reads ``status_filter`` — the prior ``status`` key was
        // silently dropped by FastAPI, leaving the filter tabs in
        // InsightSection inert on the Jobs detail page.
        ...(status && status.length > 0 && { status_filter: status.join(",") }),
        ...(sortBy && { sort: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      });
    },
    select: response => response?.data,
    enabled: Boolean(resourceUri),
  });
};

export * from "./autoTuneHooks";
export * from "./sparkHooks";
